import { Card, CardBody, Chip, Input, Progress, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Tooltip } from '@heroui/react';
import { Gauge, Search } from 'lucide-react';
import React, { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentLoadError, PageTitle, PageWrapper, Spinner } from '../../../components';
import {
  AI_CHANNEL_LABELS,
  AI_CHANNELS,
  AiChannel,
  OrgUsageRow,
  useGetAiUsageOverview,
} from '../../../queries';

/**
 * Zużycie AI w rozbiciu na kanały, wiersz na organizację.
 *
 * Wiersz opisuje NAZWA HOTELU, nie id organizacji: to widok kosztowy, czytany
 * przez człowieka. Id zostaje wyłącznie jako cel przejścia do limitów.
 *
 * Liczby pochodzą z tego samego agregatora co raporty mailowe, więc panel
 * i mail nie mogą pokazywać różnych kwot.
 */

const usd = (value: number): string => `$${value.toFixed(2)}`;

/** Od 80 procent budżet wymaga uwagi, od 100 kanał jest już zablokowany. */
const usageColor = (percent: number): 'danger' | 'warning' | 'primary' => {
  if (percent >= 100) return 'danger';
  if (percent >= 80) return 'warning';
  return 'primary';
};

/** Rozbicie kosztu w kanale — pokazywane w dymku nad kwotą. */
const breakdownText = (row: OrgUsageRow, channel: AiChannel): string => {
  const b = row.channels[channel].breakdown;
  const parts = [
    ['czat', b.chatUsd],
    ['synteza', b.ttsUsd],
    ['transkrypcja', b.sttUsd],
    ['rozmowy live', b.realtimeUsd],
  ].filter(([, value]) => (value as number) > 0);

  if (!parts.length) return 'Brak zarejestrowanego zużycia';
  return parts.map(([label, value]) => `${label}: ${usd(value as number)}`).join(', ');
};

export const AiUsageView: FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useGetAiUsageOverview();
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((row) => row.hotelName.toLowerCase().includes(needle));
  }, [data, search]);

  if (isLoading) return <Spinner global />;
  if (error || !data) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message={error?.message} />
      </PageWrapper>
    );
  }

  const atRisk = data.rows.filter((row) => row.totalLimitUsd > 0 && row.usagePercent >= 80).length;

  return (
    <PageWrapper>
      <PageTitle title="Użycie AI" />

      <p className="text-sm text-default-500 mt-2 mb-5 max-w-[820px]">
        Zużycie w dolarach od ostatniego resetu, w rozbiciu na kanały. Liczone po stawkach
        z zakładki Cennik AI. Kliknij wiersz, żeby przejść do limitów organizacji.
      </p>

      <div className="flex flex-wrap gap-4 mb-5">
        <Card shadow="sm" className="min-w-[180px]">
          <CardBody>
            <p className="text-tiny text-default-500">Organizacje z budżetem</p>
            <p className="text-xl font-semibold">{data.totals.organizations}</p>
          </CardBody>
        </Card>
        <Card shadow="sm" className="min-w-[180px]">
          <CardBody>
            <p className="text-tiny text-default-500">Łączne zużycie</p>
            <p className="text-xl font-semibold">{usd(data.totals.usageUsd)}</p>
          </CardBody>
        </Card>
        <Card shadow="sm" className="min-w-[180px]">
          <CardBody>
            <p className="text-tiny text-default-500">Łączne budżety</p>
            <p className="text-xl font-semibold">{usd(data.totals.limitUsd)}</p>
          </CardBody>
        </Card>
        <Card shadow="sm" className="min-w-[180px]">
          <CardBody>
            <p className="text-tiny text-default-500">Budżet na wyczerpaniu</p>
            <p className={`text-xl font-semibold ${atRisk ? 'text-warning' : ''}`}>{atRisk}</p>
          </CardBody>
        </Card>
      </div>

      <Input
        className="max-w-[320px] mb-4"
        placeholder="Szukaj po nazwie hotelu"
        value={search}
        onValueChange={setSearch}
        startContent={<Search size={14} className="text-default-400" />}
        isClearable
        onClear={() => setSearch('')}
      />

      <Table aria-label="Zużycie AI organizacji" removeWrapper>
        <TableHeader>
          <TableColumn>Hotel</TableColumn>
          <TableColumn align="end">{AI_CHANNEL_LABELS.chat}</TableColumn>
          <TableColumn align="end">{AI_CHANNEL_LABELS.tts}</TableColumn>
          <TableColumn align="end">{AI_CHANNEL_LABELS.realtime}</TableColumn>
          <TableColumn align="end">Razem</TableColumn>
          <TableColumn>Budżet</TableColumn>
          <TableColumn align="center">Reset</TableColumn>
        </TableHeader>
        <TableBody emptyContent="Brak organizacji ze skonfigurowanymi budżetami AI">
          {rows.map((row) => (
            <TableRow
              key={row.orgId}
              className="cursor-pointer hover:bg-default-100"
              onClick={() => navigate(`/organizations/${row.orgId}/ai-limits`)}
            >
              <TableCell>
                <span className="font-medium">{row.hotelName}</span>
              </TableCell>

              {AI_CHANNELS.map((channel) => (
                <TableCell key={channel} className="text-right">
                  <Tooltip content={breakdownText(row, channel)} placement="top">
                    <span>{usd(row.channels[channel].usageUsd)}</span>
                  </Tooltip>
                </TableCell>
              ))}

              <TableCell className="text-right font-semibold">{usd(row.totalUsageUsd)}</TableCell>

              <TableCell>
                {row.totalLimitUsd > 0 ? (
                  <div className="min-w-[140px]">
                    <div className="flex justify-between text-tiny text-default-500 mb-1">
                      <span>{usd(row.totalLimitUsd)}</span>
                      <span>
                        {row.usagePercent}
                        %
                      </span>
                    </div>
                    <Progress
                      aria-label="Wykorzystanie budżetu"
                      value={Math.min(100, row.usagePercent)}
                      color={usageColor(row.usagePercent)}
                      size="sm"
                    />
                  </div>
                ) : (
                  <span className="text-tiny text-default-400">brak budżetu</span>
                )}
              </TableCell>

              <TableCell className="text-center">
                <Chip
                  size="sm"
                  variant="flat"
                  color={row.autoResetMonthly ? 'success' : 'default'}
                  startContent={<Gauge size={12} />}
                >
                  {row.autoResetMonthly ? 'miesięczny' : 'wyłączony'}
                </Chip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PageWrapper>
  );
};
